import { Paragraph, useToast } from "@aidkitorg/component-library";
import { ConversationItemCreateEvent, ConversationItemWithReference, RealtimeServerEvent, ResponseCreateEvent } from "openai/resources/beta/realtime/realtime";
import React, { PropsWithChildren, useContext, useEffect, useReducer, useState } from "react";
import { usePost } from "../API";
import { LoggedInConfigurationContext, PublicConfigurationContext } from "../Context";
import { safeParse } from "../Util";
import { initializeRTC } from "./RTCConnection";

type AICoachState = {
  enabled: boolean,
  isInitializing: boolean,
  setIsInitializing: (v: AICoachState["isInitializing"]) => void,
  hasAcceptedTerms: boolean,
  setHasAcceptedTerms: (v: AICoachState["hasAcceptedTerms"]) => void,
  modality: "text" | "audio",
  setModality: (v: AICoachState["modality"]) => void,
  audioElement: HTMLAudioElement | null,
  setAudioElement: (v:AICoachState["audioElement"]) => void,
  peerConnection: RTCPeerConnection | null,
  setPeerConnection: (v:AICoachState["peerConnection"]) => void,
  dataChannel: RTCDataChannel | null,
  setDataChannel: (v:AICoachState["dataChannel"]) => void,
  channelIsOpen: () => boolean,
  startSession: () => Promise<void>,
  stopSession: () => void,
  sendMessage: (msg:string) => void,
  transcript: AICoachTranscript
};

const defaults : AICoachState = {
  enabled: false,
  isInitializing: false,
  setIsInitializing: () => {},
  hasAcceptedTerms: false,
  setHasAcceptedTerms: () => {},
  modality: "text",
  setModality: () => {},
  audioElement: null,
  setAudioElement: () => {},
  peerConnection: null,
  setPeerConnection: () => {},
  dataChannel: null,
  setDataChannel: () => {},
  channelIsOpen: () => false,
  startSession: async () => {},
  stopSession: () => {},
  sendMessage: () => {},
  transcript: {
    list: [],
    map: new Map()
  }
}

export const AICoachContext = React.createContext<AICoachState>(defaults);

type WithAKMeta = {
  // these are custom values that we append to all conversation items
  meta: {
    time: number
  }
}

type AICoachConversationItem = ConversationItemWithReference & WithAKMeta;

type AddUserMessage = {
  type: "addUserMessage",
  payload: AICoachConversationItem 
};

type TranscriptAction = RealtimeServerEvent | AddUserMessage;

function transcriptReducer(state: AICoachTranscript, action: TranscriptAction) {  
  let { map } = state;
  switch(action.type) {
    /* 
    CLIENT EVENTS
    */
    case "addUserMessage":
      if (action.payload.id) {
        map.set(action.payload.id, action.payload);
      }
      break;
      /* 
    SERVER EVENTS
    */
    case "response.output_item.added":
      // server added a new response
      if (action.response_id) {
        map.set(action.response_id, {
          ...action.item,
          meta: {
            time: Date.now()
          }
        });
      }
      break;
    case "response.text.delta":
      const item = action.response_id ? map.get(action.response_id) : undefined;
      if (item) {
        const content = item.content = item.content ?? [];
        content[action.content_index] = content[action.content_index] ?? {};
        content[action.content_index].text = [content[action.content_index].text ?? "", action.delta].join("");
      }
      break;
    case "response.output_item.done":
      // server has completed the response
  }
  return {
    list: Array.from(map.values()),
    map
  };  
}


type AICoachTranscript = {
  list: AICoachConversationItem[],
  map: Map<string, AICoachConversationItem>
}

export function AICoachProvider({ children }: PropsWithChildren ) {
  const createCoachSession = usePost("/coach/conversation");
  const { toast } = useToast();
  const config = useContext(PublicConfigurationContext);
  const enabled = config?.experimental?.enableAICoach!!
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState<AICoachState["hasAcceptedTerms"]>(false);
  const [modality, setModality] = useState<AICoachState["modality"]>("text");
  const [peerConnection, setPeerConnection] = useState<AICoachState["peerConnection"]>(null);
  const [dataChannel, setDataChannel] = useState<AICoachState["dataChannel"]>(null);
  const [audioElement, setAudioElement] = useState<AICoachState["audioElement"]>(null);
  const [isInitializing, setIsInitializing] = useState<AICoachState["isInitializing"]>(false);
  const [transcript, transcriptDispatch] = useReducer(transcriptReducer, defaults.transcript as AICoachTranscript);
  
  // if we have both an audio element and a peer connection
  // bind the audio to the peer connection
  useEffect(
    () => {
      if (audioElement && peerConnection) {
        peerConnection.ontrack = e => {
          if (audioElement) {
            audioElement.srcObject = e.streams[0];
          }
        }
      }
    },
    [audioElement, peerConnection]
  );

  function dataChannelMessageHandler(msg: MessageEvent) {
    const event = safeParse(msg.data) as RealtimeServerEvent;
    
    console.debug("Received WebRTC event:")
    if (event.type === 'error') {
      console.error(event);
    }
    else {
      console.debug(event);
    }

    if (event?.type) {
      transcriptDispatch(event as TranscriptAction);
    }
    else {
      console.error('Received invalid event');
    }
  }

  async function startSession() {
    try {
      if (isInitializing) {
        console.warn("Session is already initializing");
        return;
      }
      setIsInitializing(true);
      const { token } = await createCoachSession({ modality, persistConversation: true });
      if (token) {
        const { pc, dataChannel } = await initializeRTC(token, {
          onReady: function onReady() {
            // only set channel to be available to consumers once it's ready
            setDataChannel(dataChannel);
            dataChannel.addEventListener("message", dataChannelMessageHandler);
  
          },
          onConnectionError: function onConnectionError(errMsg:string) {
            toast({
              title: "Connection error",
              variant: "error",
              description: <Paragraph>{errMsg}</Paragraph>,
            });
          }
        });
        setPeerConnection(pc);
      }  
    }
    finally {
      setIsInitializing(false);
    }
  }

  function stopSession() { 
    // TODO, also close microphone if open
    peerConnection?.close();
    dataChannel?.close();    
    if (audioElement) {
      audioElement.pause();
      audioElement.srcObject = null;
    }

    setPeerConnection(null);
    setDataChannel(null);
  }

  function channelIsOpen(){
    return dataChannel?.readyState === "open";
  }

  function sendMessage(text:string) {    
    if (!dataChannel || !channelIsOpen()) {
      console.error("Failed to send message - RTCDataChannel is not open - current state:", dataChannel?.readyState ?? "Unknown");
      return;
    }
    const now = Date.now();

    // generate a (mostly) collision proof ID that creates a natural sort order.
    // - 1743692588848_000
    // - 1743692588848_001
    // - 1743692588849_000
    let existingItemWithTimestamp;
    let dedupeIndex = 0;
    let id : string | undefined;
    do {
      id = `${now}_${dedupeIndex.toString().padStart(3, '0')}`;
      existingItemWithTimestamp = transcript.map.get(`convo_${id}`);
      dedupeIndex += 1;
    } while(existingItemWithTimestamp);

    const conversationItem = {
      id,
      type: "message",
      role: "user",
      content: [
        {
          type: "input_text",
          text
        }
      ]
    } as ConversationItemWithReference;

    // after creating a "conversation.item.create" event
    // you must also trigger a "response.create" 
    // event to elicit a response from the assistant
    dataChannel.send(JSON.stringify({
      event_id: `convo_${id}`,
      type: "conversation.item.create",
      item: conversationItem
    } as ConversationItemCreateEvent));

    dataChannel.send(JSON.stringify({
      event_id: `resp_${id}`,
      type: "response.create"
    } as ResponseCreateEvent));

    // update local transcript
    transcriptDispatch({
      type: "addUserMessage",
      payload: {
        ...conversationItem,
        meta: {
          time: now
        }
      }
    });
  }  

  const stateValue : AICoachState = {
    enabled,
    isInitializing, setIsInitializing,
    hasAcceptedTerms, setHasAcceptedTerms,
    modality, setModality,
    peerConnection, setPeerConnection,
    dataChannel, setDataChannel,
    audioElement, setAudioElement,
    channelIsOpen,
    startSession,
    stopSession,
    sendMessage,
    transcript
  };

  return <AICoachContext.Provider value={stateValue}>
    {children}
  </AICoachContext.Provider>;
}

// sugar
export function useAICoach() : AICoachState {
  const AICoach = useContext(AICoachContext);
  return AICoach;
}